// Ring 0 — sole writer to RunState.pocket (HAND-05 / RESEARCH Anti-Pattern 7).
//
// All four mutating methods (tryAdd, swap, discard, clear) emit
// `pocket:changed` on a Phaser.Events.EventEmitter bound at runtime by
// `bindPocketBus(emitter)`. The HUD (RunUIScene) and any other observer
// MUST subscribe to that event rather than reading `RunState.pocket`
// directly each frame.
//
// `import type Phaser` keeps this module Ring 0 — no runtime Phaser
// values are pulled in, so the file compiles in a Node test environment
// and the Plan-B hot-swap to `@poker-apprentice/hand-evaluator` (CONTEXT)
// remains mechanical.
//
// JSON-serialization invariant (D-19): event payloads use
// `RunState.pocket.slice()` so the event consumer never aliases the live
// array. A consumer that mutated the payload would otherwise corrupt
// RunState; the slice keeps the boundary clean.
//
// PITFALL #7 (audience-fatal) is mitigated at the call site (RunScene
// destroy()s the pickup the moment tryAdd succeeds), not here — this
// module simply caps the pocket at POCKET_CAPACITY=7 and reports
// success/failure via the boolean return of tryAdd.

import type Phaser from 'phaser';
import { RunState } from '../run/RunState';
import type { Card } from '../run/types';

export const POCKET_CAPACITY = 7;

let bus: Phaser.Events.EventEmitter | null = null;

export function bindPocketBus(emitter: Phaser.Events.EventEmitter): void {
  bus = emitter;
}

export const PocketSystem = {
  tryAdd(card: Card): boolean {
    if (RunState.pocket.length >= POCKET_CAPACITY) return false;
    RunState.pocket.push(card);
    bus?.emit('pocket:changed', { pocket: RunState.pocket.slice() });
    return true;
  },
  swap(slotIdx: number, newCard: Card): void {
    if (slotIdx < 0 || slotIdx >= RunState.pocket.length) return;
    RunState.pocket[slotIdx] = newCard;
    bus?.emit('pocket:changed', { pocket: RunState.pocket.slice() });
  },
  discard(slotIdx: number): void {
    if (slotIdx < 0 || slotIdx >= RunState.pocket.length) return;
    RunState.pocket.splice(slotIdx, 1);
    bus?.emit('pocket:changed', { pocket: RunState.pocket.slice() });
  },
  clear(): void {
    RunState.pocket = [];
    bus?.emit('pocket:changed', { pocket: [] });
  },
};
